import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  TrendingUp, 
  Eye,
  MousePointerClick,
  Target,
  Search,
  Loader2,
  BarChart3,
  Globe,
  MapPin,
  Link2,
  Unlink,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

function GoogleConnectionCard({ clientId, googleStatus, onConnect, onDisconnect, loading }) {
  if (!clientId) return null;

  return (
    <div className="bg-[#151B2B] rounded-xl p-6 border border-[#2A3441]" data-testid="google-connection-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            googleStatus?.connected ? 'bg-[#10B981]/10' : 'bg-[#F59E0B]/10'
          }`}>
            {googleStatus?.connected 
              ? <CheckCircle2 className="h-6 w-6 text-[#10B981]" />
              : <Link2 className="h-6 w-6 text-[#F59E0B]" />
            }
          </div>
          <div>
            <h3 className="text-white font-semibold">Google Analytics & Search Console</h3>
            <p className="text-[#94A3B8] text-sm mt-0.5">
              {googleStatus?.connected 
                ? `Connecte le ${new Date(googleStatus.connected_at).toLocaleDateString('fr-FR')}`
                : "Connectez votre compte Google pour voir vos vraies statistiques"
              }
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {googleStatus?.connected ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onDisconnect}
              disabled={loading}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
              data-testid="google-disconnect-btn"
            >
              <Unlink className="h-4 w-4 mr-2" />
              Deconnecter
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onConnect}
              disabled={loading}
              className="bg-[#0099CC] hover:bg-[#0088BB] text-white"
              data-testid="google-connect-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              Connecter Google Analytics
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SyncBar({ googleConnected, onSync, syncing }) {
  if (!googleConnected) return null;
  return (
    <div className="flex items-center justify-between bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl px-5 py-3" data-testid="sync-bar">
      <span className="text-[#10B981] text-sm font-medium flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4" />
        Google connecte — Donnees reelles disponibles
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={onSync}
        disabled={syncing}
        className="border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/10"
        data-testid="sync-google-btn"
      >
        {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
        Synchroniser
      </Button>
    </div>
  );
}

export default function SEOPage() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, clientId: userClientId } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId || "");
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Handle OAuth callback params
  useEffect(() => {
    if (searchParams.get("google_connected") === "true") {
      toast.success("Google Analytics connecte avec succes !");
      searchParams.delete("google_connected");
      setSearchParams(searchParams, { replace: true });
      checkGoogleStatus();
    }
    if (searchParams.get("google_error")) {
      const err = searchParams.get("google_error");
      toast.error(`Erreur de connexion Google: ${err}`);
      searchParams.delete("google_error");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchSEOData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchSEOData = async (pid) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/seo/${pid}/overview`);
      setSeoData(response.data);
      // Check Google status based on project's client_id
      const project = projects.find(p => p.id === pid);
      if (project) {
        checkGoogleStatus(project.client_id);
      }
    } catch (error) {
      console.error("Error fetching SEO data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkGoogleStatus = useCallback(async (cid) => {
    try {
      const targetClientId = cid || userClientId;
      const params = targetClientId ? `?client_id=${targetClientId}` : "";
      const resp = await axios.get(`${API}/oauth/google/status${params}`);
      setGoogleStatus(resp.data);
    } catch {
      setGoogleStatus({ connected: false });
    }
  }, [userClientId]);

  // Also check google status when projects load
  useEffect(() => {
    if (selectedProject && projects.length > 0) {
      const project = projects.find(p => p.id === selectedProject);
      if (project) {
        checkGoogleStatus(project.client_id);
      }
    }
  }, [selectedProject, projects, checkGoogleStatus]);

  const handleGoogleConnect = async () => {
    setGoogleLoading(true);
    try {
      const project = projects.find(p => p.id === selectedProject);
      const targetClientId = project?.client_id || userClientId;
      const params = targetClientId ? `?client_id=${targetClientId}` : "";
      const resp = await axios.get(`${API}/oauth/google/authorize${params}`);
      // Redirect to Google OAuth in same window
      window.location.href = resp.data.authorization_url;
    } catch (error) {
      toast.error("Erreur lors de la connexion Google");
      setGoogleLoading(false);
    }
  };

  const handleGoogleDisconnect = async () => {
    setGoogleLoading(true);
    try {
      const project = projects.find(p => p.id === selectedProject);
      const targetClientId = project?.client_id || userClientId;
      const params = targetClientId ? `?client_id=${targetClientId}` : "";
      await axios.delete(`${API}/oauth/google/disconnect${params}`);
      setGoogleStatus({ connected: false });
      toast.success("Google Analytics deconnecte");
    } catch (error) {
      toast.error("Erreur lors de la deconnexion");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSync = async () => {
    if (!selectedProject) return;
    setSyncing(true);
    try {
      const resp = await axios.post(`${API}/seo/${selectedProject}/sync-google`);
      toast.success(`Synchronisation terminee ! ${resp.data.errors?.length ? `(${resp.data.errors.length} avertissement(s))` : ""}`);
      if (resp.data.errors?.length) {
        resp.data.errors.forEach(e => toast.warning(e));
      }
      // Refresh SEO data
      fetchSEOData(selectedProject);
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur de synchronisation";
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const currentProject = projects.find(p => p.id === selectedProject);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="seo-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isAdmin ? "SEO & Rapports" : "Mon SEO"}
          </h1>
          <p className="text-[#94A3B8] mt-1">
            Suivez vos performances Google
          </p>
        </div>
        
        {projects.length > 1 && (
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-[250px] bg-[#151B2B] border-[#2A3441] text-white">
              <Globe className="h-4 w-4 mr-2 text-[#0099CC]" />
              <SelectValue placeholder="Selectionnez un projet" />
            </SelectTrigger>
            <SelectContent className="bg-[#151B2B] border-[#2A3441]">
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Project Info */}
      {currentProject && (
        <div className="bg-[#151B2B] rounded-xl p-4 border border-[#2A3441] flex items-center gap-4">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#0B0F19]">
            {currentProject.screenshot?.url && (
              <img src={currentProject.screenshot.url} alt={currentProject.name} className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-white font-semibold">{currentProject.name}</h2>
            <a href={currentProject.url} target="_blank" rel="noopener noreferrer" className="text-[#0099CC] text-sm hover:underline flex items-center gap-1">
              {currentProject.url}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      )}

      {/* Google Connection Card */}
      <GoogleConnectionCard
        clientId={currentProject?.client_id || userClientId}
        googleStatus={googleStatus}
        onConnect={handleGoogleConnect}
        onDisconnect={handleGoogleDisconnect}
        loading={googleLoading}
      />

      {/* Sync Bar */}
      <SyncBar
        googleConnected={googleStatus?.connected}
        onSync={handleSync}
        syncing={syncing}
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : seoData && !seoData.google_connected && !seoData.ga4?.sessions ? (
        <div className="text-center py-16 bg-[#151B2B] rounded-xl border border-[#2A3441]" data-testid="seo-empty-state">
          <div className="w-16 h-16 rounded-full bg-[#F59E0B]/10 mx-auto flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-[#F59E0B]" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Aucune donnee SEO disponible</h3>
          <p className="text-[#94A3B8] max-w-md mx-auto mb-6">
            Connectez Google Analytics et Search Console pour voir les statistiques reelles de votre site.
          </p>
          {!googleStatus?.connected && (
            <Button
              onClick={handleGoogleConnect}
              disabled={googleLoading}
              className="bg-[#0099CC] hover:bg-[#0088BB] text-white"
              data-testid="seo-connect-google-btn"
            >
              {googleLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              Connecter Google Analytics
            </Button>
          )}
        </div>
      ) : seoData ? (
        <>

          {/* Sync Errors */}
          {seoData.sync_errors?.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">
              <p className="font-semibold mb-1">Avertissements de synchronisation :</p>
              {seoData.sync_errors.map((err, i) => (
                <p key={i}>- {err}</p>
              ))}
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-[#151B2B] rounded-xl p-6 border border-[#2A3441] card-premium" data-testid="stat-sessions">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center">
                  <Eye className="h-6 w-6 text-[#3B82F6]" />
                </div>
                <TrendingUp className="h-5 w-5 text-[#10B981]" />
              </div>
              <p className="text-3xl font-bold text-white">{seoData.ga4?.sessions?.toLocaleString() || 0}</p>
              <p className="text-[#64748b] text-sm mt-1">Sessions / mois</p>
            </div>

            <div className="bg-[#151B2B] rounded-xl p-6 border border-[#2A3441] card-premium" data-testid="stat-clicks">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                  <MousePointerClick className="h-6 w-6 text-[#10B981]" />
                </div>
                <TrendingUp className="h-5 w-5 text-[#10B981]" />
              </div>
              <p className="text-3xl font-bold text-white">{seoData.gsc?.clicks?.toLocaleString() || 0}</p>
              <p className="text-[#64748b] text-sm mt-1">Clics Google</p>
            </div>

            <div className="bg-[#151B2B] rounded-xl p-6 border border-[#2A3441] card-premium" data-testid="stat-impressions">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                  <Search className="h-6 w-6 text-[#F59E0B]" />
                </div>
              </div>
              <p className="text-3xl font-bold text-white">{seoData.gsc?.impressions?.toLocaleString() || 0}</p>
              <p className="text-[#64748b] text-sm mt-1">Impressions</p>
            </div>

            <div className="bg-[#151B2B] rounded-xl p-6 border border-[#2A3441] card-premium" data-testid="stat-position">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                  <Target className="h-6 w-6 text-[#8B5CF6]" />
                </div>
              </div>
              <p className="text-3xl font-bold text-white">{seoData.gsc?.position?.toFixed(1) || 0}</p>
              <p className="text-[#64748b] text-sm mt-1">Position moyenne</p>
            </div>
          </div>

          {/* Keywords Table */}
          {seoData.keywords?.length > 0 && (
            <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] overflow-hidden" data-testid="keywords-table">
              <div className="p-6 border-b border-[#2A3441]">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[#0099CC]" />
                  Mots-cles
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#0B0F19]">
                      <th className="text-left text-[#94A3B8] text-sm font-medium px-6 py-4">Mot-cle</th>
                      <th className="text-left text-[#94A3B8] text-sm font-medium px-6 py-4">Ville</th>
                      <th className="text-right text-[#94A3B8] text-sm font-medium px-6 py-4">Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seoData.keywords?.map((kw, index) => (
                      <tr key={index} className="border-t border-[#2A3441] hover:bg-[#0B0F19]/50">
                        <td className="px-6 py-4 text-white">{kw.keyword}</td>
                        <td className="px-6 py-4">
                          <span className="flex items-center gap-2 text-[#94A3B8]">
                            <MapPin className="h-4 w-4 text-[#0099CC]" />
                            {kw.city || "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`font-bold ${kw.position <= 5 ? 'text-[#10B981]' : kw.position <= 10 ? 'text-[#F59E0B]' : 'text-[#94A3B8]'}`}>
                            #{kw.position}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CTR Info */}
          {seoData.gsc?.ctr > 0 && (
            <div className="bg-[#151B2B] rounded-xl p-6 border border-[#2A3441]" data-testid="ctr-info">
              <h3 className="text-white font-semibold mb-4">Performance de clics</h3>
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-[#0B0F19] rounded-full h-4 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#0099CC] to-[#10B981]" 
                    style={{ width: `${(seoData.gsc?.ctr || 0) * 10}%` }}
                  />
                </div>
                <span className="text-white font-bold">{(seoData.gsc?.ctr || 0).toFixed(1)}%</span>
              </div>
              <p className="text-[#64748b] text-sm mt-2">
                Taux de clics (CTR) — Pourcentage de personnes qui cliquent apres avoir vu votre site dans Google
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <BarChart3 className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucune donnee SEO</h3>
          <p className="text-[#94A3B8]">
            Selectionnez un projet pour voir ses statistiques
          </p>
        </div>
      )}
    </div>
  );
}
